import {
  CarePlanGoalResponse,
  CarePlanProblemResponse,
} from '~/lib/types/carePlan/carePlans';
import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  FAIconName,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  TagDropdown,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import {
  buildGoalStatusTagDropdownProps,
  getCarePlanGoalTagDropdownOptions,
  toCarePlanGoalTagDropdownOptions,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';
import { CarePlanGoalCardManifestationRow } from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalCardManifestationRow';
import { useEffect, useMemo } from 'react';
import { useUpdateGoalMutation } from '~/store/services/carePlan';
import { useGetProblemTypesGoalStatusesMapQuery } from '~/store/services/refCarePlanEnums';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';
import { CarePlanInterventionCollapse } from '~/components/maud/patients/profile/tabs/careplan/CarePlanInterventionCollapse';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CARE_PLAN_INTERVENTION_SECTION_LABELS } from '~/lib/constants/carePlan.constants';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { CarePlanGoalCardProblemRow } from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalCardProblemRow';
import { CarePlanDescriptionRow } from './CarePlanDescriptionRow';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  carePlanId: string;
  treatmentEpisodeId: string;
  problem: CarePlanProblemResponse;
  onEditClick: (goal: CarePlanGoalResponse) => void;
  onEditProblemClick: (problem: CarePlanProblemResponse) => void;
  onRemoveClick: (problemId: string) => void;
};

export const CarePlanGoalCard = ({
  treatmentEpisodeId,
  carePlanId,
  problem,
  onEditClick,
  onEditProblemClick,
  onRemoveClick,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const {
    carePlanNotesDisplay: isCarePlanNotesDisplayEnabled,
    carePlanM4CustomInterventions,
    carePlanM4MedPsychProblems: isMedPsychProblemsEnabled,
  } = useFlags<LDFlags>();

  const { data: problemTypesGoalStatusesMap } =
    useGetProblemTypesGoalStatusesMapQuery();

  // Co-occurring goals need med/psych statuses from reference data; ED goals
  // keep the hardcoded lifecycle list so their ordering doesn't drift with the
  // API response.
  const statusDropdownOptions = useMemo(() => {
    const isCoOccurring =
      !!problem.problemType &&
      problem.problemType !== ProblemTypeCode.EATING_DISORDER;
    if (isCoOccurring) {
      return toCarePlanGoalTagDropdownOptions(
        problemTypesGoalStatusesMap?.[problem.problemType] ?? [],
        isMedPsychProblemsEnabled,
      );
    }
    return getCarePlanGoalTagDropdownOptions(isMedPsychProblemsEnabled);
  }, [
    problem.problemType,
    problemTypesGoalStatusesMap,
    isMedPsychProblemsEnabled,
  ]);

  const [updateGoal, { error }] = useUpdateGoalMutation();

  const { createToast } = useToastMessage();

  useEffect(() => {
    if (error) {
      createToast({
        id: 'goal-card-update-error',
        text: "An error occurred updating the goal's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [error]);

  const goalMenuOptions: FireflyKebabMenuItem[] = [
    {
      label: 'Edit goal',
      leftIcon: FAIconName.penRegular,
      onClick: () => {
        trackEvent(CarePlanEvents.clickOnEditGoalKebabMenuOption);
        onEditClick(problem.goals[0]);
      },
    },
    {
      label: 'Remove goal',
      leftIcon: FAIconName.trashRegular,
      leftIconColor: 'critical.base',
      onClick: () => {
        trackEvent(CarePlanEvents.clickOnRemoveGoalKebabMenuOption);
        onRemoveClick(problem.id);
      },
    },
  ];

  const sortedManifestations = useMemo(
    () =>
      problem.manifestations.toSorted(
        (m1, m2) =>
          m1.pathwayManifestation.sortOrder - m2.pathwayManifestation.sortOrder,
      ),
    [problem.manifestations],
  );

  const goal = problem.goals[0];

  // Filter out custom interventions if feature flag is off
  const interventionsToDisplay = useMemo(() => {
    if (!goal) return [];
    return carePlanM4CustomInterventions
      ? goal.interventions
      : goal.interventions.filter((intervention) => !intervention.isCustom);
  }, [goal, carePlanM4CustomInterventions]);

  if (problem.goals?.length === 0) {
    return <></>;
  }

  const isDescriptionPresent = Boolean(
    goal.descriptionDetails?.description?.trim(),
  );

  return (
    <ChakraFlex
      borderColor="neutral.line"
      borderRadius="12px"
      borderWidth="1px"
      direction="column"
      padding="18px 0"
    >
      <ChakraBox paddingX="18px">
        <ChakraGrid
          columnGap="12px"
          templateColumns="1fr auto auto"
          templateRows="1fr"
          width="100%"
        >
          <Text variant="h5">{goal.pathwayGoal.label}</Text>
          <TagDropdown
            id={`pathway-${carePlanId}-problem-${problem.pathwayProblem.code}-status-dropdown`}
            menuOptions={statusDropdownOptions}
            onDropdownOpen={() =>
              trackEvent(CarePlanEvents.clickOnGoalStatusMenu)
            }
            onOptionClick={(newStatus) => {
              trackEvent(CarePlanEvents.clickOnGoalStatusMenuOption);
              updateGoal({
                treatmentEpisodeId,
                goalId: goal.id,
                body: {
                  description: goal.description,
                  statusCode: newStatus as CarePlanGoalStatus,
                },
              });
            }}
            {...buildGoalStatusTagDropdownProps(
              goal.status.code,
              goal.status.label,
              statusDropdownOptions,
            )}
          />
          <FireflyKebabMenu
            aria-label={`Options menu for ${problem.goals[0].pathwayGoal.label}`}
            onMenuOpen={() => trackEvent(CarePlanEvents.clickOnGoalKebabMenu)}
            options={goalMenuOptions}
          />
        </ChakraGrid>
        {isCarePlanNotesDisplayEnabled && isDescriptionPresent ? (
          <CarePlanDescriptionRow
            descriptionDetails={goal.descriptionDetails}
            goalId={goal.id}
          />
        ) : (
          goal.description && <Text marginTop="6px">{goal.description}</Text>
        )}
        {sortedManifestations.length > 0 && (
          <ChakraFlex direction="column" gap="18px" marginTop="18px">
            {sortedManifestations.map((manifestation) => (
              <CarePlanGoalCardManifestationRow
                key={`${problem.id}-${manifestation.pathwayManifestation.code}`}
                manifestation={manifestation}
                onEditProblemClick={() => onEditProblemClick(problem)}
                problem={problem}
              />
            ))}
          </ChakraFlex>
        )}
        {sortedManifestations.length === 0 && (
          <CarePlanGoalCardProblemRow
            onEditProblemClick={onEditProblemClick}
            problem={problem}
          />
        )}
      </ChakraBox>
      {interventionsToDisplay.length > 0 && (
        <ChakraBox marginTop="18px" paddingLeft="10px" paddingRight="18px">
          <CarePlanInterventionCollapse
            interventions={interventionsToDisplay}
            label={CARE_PLAN_INTERVENTION_SECTION_LABELS.RELATED_INTERVENTIONS}
            openByDefault
            treatmentEpisodeId={treatmentEpisodeId}
          />
        </ChakraBox>
      )}
    </ChakraFlex>
  );
};
